/**
 * The record type filter: several types at once, matching a contact that has
 * any of them. PostgREST `in`.
 *
 * Bill works from "Source and Potential Investor together" (LCCRM-86), which a
 * single-select filter could not show.
 */
export const RECORD_TYPE_FILTER_KEY = "record_type@in";

/**
 * The key the filter used while it was single-select. Still read, so a saved
 * list filter or a bookmarked URL from before keeps its selection, but never
 * written.
 */
export const LEGACY_RECORD_TYPE_KEY = "record_type";

type FilterValues = Record<string, unknown>;

const BARE = /^[A-Za-z0-9_-]+$/;

/**
 * Serialises values the way PostgREST reads an `in` list: `(a,b)`. A value
 * with anything but letters, digits, `-` and `_` is double-quoted, which is
 * what PostgREST needs for commas and spaces. Values made by Settings are
 * slugs, so in practice nothing is quoted.
 */
export function serializeRecordTypes(values: readonly string[]): string {
  return `(${values
    .map((value) =>
      BARE.test(value) ? value : `"${value.replace(/(["\\])/g, "\\$1")}"`,
    )
    .join(",")})`;
}

export function parseRecordTypes(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const inner = value.trim().replace(/^\(/, "").replace(/\)$/, "");
  const parsed: string[] = [];
  let current = "";
  let quoted = false;
  let escaped = false;
  for (const char of inner) {
    if (escaped) {
      current += char;
      escaped = false;
    } else if (quoted && char === "\\") {
      escaped = true;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      parsed.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  parsed.push(current);
  return parsed.map((part) => part.trim()).filter((part) => part !== "");
}

/** The selected record types, from whichever key is present, without repeats. */
export function readRecordTypeFilter(
  filterValues: FilterValues | undefined,
): string[] {
  const values = filterValues ?? {};
  const legacy = values[LEGACY_RECORD_TYPE_KEY];
  return [
    ...new Set([
      ...parseRecordTypes(values[RECORD_TYPE_FILTER_KEY]),
      ...(typeof legacy === "string" && legacy !== "" ? [legacy] : []),
    ]),
  ];
}

/**
 * Returns the next filter object. Both keys are dropped first, so the old
 * single-select key never stays behind to be ANDed with the new one, and an
 * empty selection removes the filter rather than sending `()`.
 */
export function writeRecordTypeFilter(
  filterValues: FilterValues | undefined,
  selected: readonly string[],
): FilterValues {
  const next = { ...(filterValues ?? {}) };
  delete next[RECORD_TYPE_FILTER_KEY];
  delete next[LEGACY_RECORD_TYPE_KEY];
  if (selected.length > 0) {
    next[RECORD_TYPE_FILTER_KEY] = serializeRecordTypes(selected);
  }
  return next;
}

export function toggleRecordType(
  selected: readonly string[],
  value: string,
): string[] {
  return selected.includes(value)
    ? selected.filter((other) => other !== value)
    : [...selected, value];
}
